import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  EventEmitter,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import * as maplibregl from 'maplibre-gl';
import { LoaderHandlerService } from 'src/app/services';

export interface TripPoint {
  lat: number;
  lng: number;
  ts: number; // ms
  speed?: number;
  events?: { type: string; label?: string }[];
}

@Component({
  selector: 'app-moving-marker',
  templateUrl: './moving-marker.component.html',
  styleUrl: './moving-marker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MovingMarkerComponent
  implements OnInit, OnDestroy {

  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  @Input() apiKey: string = '1dj_OPqO9DwXij8EKEyC9xztDuXVWoBORCzEsQQ_Hac';
  @Input() trip: TripPoint[] = [
    { "lat": 12.97191, "lng": 77.59628, "ts": 1710000000000, "speed": 32 },
    { "lat": 12.97214, "lng": 77.59690, "ts": 1710000005000, "speed": 35 },
    { "lat": 12.97239, "lng": 77.59753, "ts": 1710000010000, "speed": 38 },
    { "lat": 12.97263, "lng": 77.59813, "ts": 1710000015000, "speed": 42 },
    { "lat": 12.97288, "lng": 77.59874, "ts": 1710000020000, "speed": 45 },
    { "lat": 12.97310, "lng": 77.59936, "ts": 1710000025000, "speed": 47 },
    { "lat": 12.97332, "lng": 77.59998, "ts": 1710000030000, "speed": 50 },
    { "lat": 12.97356, "lng": 77.60058, "ts": 1710000035000, "speed": 52 },
    { "lat": 12.97381, "lng": 77.60120, "ts": 1710000040000, "speed": 55 },
    { "lat": 12.97406, "lng": 77.60182, "ts": 1710000045000, "speed": 58 },
    { "lat": 12.97430, "lng": 77.60245, "ts": 1710000050000, "speed": 60 },
    {
      "lat": 12.97454,
      "lng": 77.60309,
      "ts": 1710000055000,
      "speed": 72,
      "events": [{ "type": "speed_violation", "label": "72 km/h in 50 zone" }]
    },
    { "lat": 12.97480, "lng": 77.60372, "ts": 1710000060000, "speed": 65 },
    { "lat": 12.97501, "lng": 77.60435, "ts": 1710000065000, "speed": 52 },
    { "lat": 12.97524, "lng": 77.60496, "ts": 1710000070000, "speed": 48 },
    {
      "lat": 12.97549,
      "lng": 77.60558,
      "ts": 1710000075000,
      "speed": 33,
      "events": [{ "type": "harsh_brake", "label": "Sudden deceleration" }]
    }
  ];
  @Input() trafficSignals: any;
  @Input() mapEvents: any;
  @Input() optimizedRouteData: any;
  @Input() telemetryAlertCoordinates: any;
  @Input() styleUrl?: string;
  @Input() playbackRate = 1.0;
  @Input() segmentDetails: any;
  @Input() tunnelBridgeData: any;
  @Input() selectedAlertIndex: number = -1;
  @Input() showAiAssist: boolean;
  @Input() tripAssistPlots: any;
  @Input() highlightedSegment: any;
  @Output() playbackStateUpdate = new EventEmitter<any>();
  @Output() isPlaying = new EventEmitter<any>();

  private map!: maplibregl.Map;
  private marker!: maplibregl.Marker;
  private rafId: number | null = null;
  private mapReady = false;
  private optimizedRouteHoverBound = false;
  private isSeeking = false;
  private lastSelectedIndex: number = -1;
  private pendingTelemetryAlerts = false;

  //Reusable popup
  private optimizedRoutePopup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 12
  });

  playing = false;
  playhead = 0;

  private lastHeading: number | null = null;
  private readonly HEADING_EPSILON_METERS = 1.5;

  get firstTs() {
    return this.trip?.length ? this.trip[0].ts : 0;
  }
  get lastTs() {
    return this.trip?.length ? this.trip[this.trip.length - 1].ts : 0;
  }

  constructor(private loaderHandler: LoaderHandlerService) { }

  ngOnInit(): void {
    if (this.trip?.length) {
      this.playhead = this.trip[0].ts;
    }
  }

  // ngAfterViewInit(): void {
  //   if (!this.trip || this.trip.length === 0) return;
  //   this.initMap();
  // }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['trip'] && changes['trip'].currentValue?.length) {
      // Update playhead
      const firstTs = this.trip[0].ts;
      const lastTs = this.trip[this.trip.length - 1].ts;

      // Initialise playhead if null/undefined
      if (this.playhead == null) {
        this.playhead = firstTs;
        this.lastHeading = null;
      }

      // If the playhead no longer fits the new trip range → reset
      if (this.playhead < firstTs || this.playhead > lastTs) {
        this.playhead = firstTs;
        this.lastHeading = null;
      }
      this.initMap();
    }
    if (changes['trafficSignals']) {
      if (this.mapReady) this.updateTrafficSignals();
    }
    if (changes['mapEvents']) {
      if (this.mapReady) this.plotMapEvents();
    }
    if (changes['optimizedRouteData']) {
      if (this.mapReady) this.updateOptimizedRoute();
    }
    if (changes['segmentDetails']) {
      if (this.mapReady) this.drawSegmentHoverLayers();
    }
    if (changes['tunnelBridgeData']) {
      if (this.mapReady) this.plotTunnelBridgeData();
    }
    if (changes['telemetryAlertCoordinates']) {
      if (this.mapReady) {
        this.plotTelemetryAlertCoordinates();
      } else {
        this.pendingTelemetryAlerts = true;
      }
    }
    if (changes['selectedAlertIndex'] && this.selectedAlertIndex > -1) {
      if (this.mapReady) this.seek(this.trip[this.selectedAlertIndex]?.ts);
    }
    if (changes['showAiAssist']) {
      this._plotTripAssistPlots();
    }
    if (changes['highlightedSegment']) {
      this.highlightPoint();
    }
  }

  private highlightPoint() {
    const sourceId = 'trip-assist-highlight-source';
    const layerId = 'trip-assist-highlight-layer';

    if (!this.highlightedSegment || !this.showAiAssist) {
      // Remove layer & source if they exist
      if (this.map?.getLayer(layerId)) this.map.removeLayer(layerId);
      if (this.map?.getSource(sourceId)) this.map.removeSource(sourceId);
      return;
    }

    const geojson: any = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [this.highlightedSegment?.lng, this.highlightedSegment?.lat]
          }
        }
      ]
    };

    // Update source if exists
    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as maplibregl.GeoJSONSource)
        .setData(geojson);
      return;
    }

    // Create source
    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson
    });

    // Create layer
    this.map.addLayer({
      id: 'trip-assist-highlight-layer',
      type: 'symbol',
      source: 'trip-assist-highlight-source',
      layout: {
        'icon-image': 'highlightedIcon',
        'icon-size': 1.5,
        'icon-allow-overlap': true
      },
      paint: {
        'icon-color': '#be2d2dff',       
        'icon-opacity': 0.9
      }
    });
  }

  private _plotTripAssistPlots() {
    const sourceId = 'trip-assist';
    const layerId = 'trip-assist-layer';
    if (!this.tripAssistPlots?.length || !this.showAiAssist) {
      // Remove layer & source if they exist
      if (this.map?.getLayer(layerId)) this.map.removeLayer(layerId);
      if (this.map?.getSource(sourceId)) this.map.removeSource(sourceId);
      return;
    }
    const features = this.tripAssistPlots.map((sig, i) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [sig.lng, sig.lat] },
      properties: { id: i, label: sig.label || '' }
    }));
    const geojson: any = {
      type: 'FeatureCollection',
      features
    };
    // Add or update source
    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      this.map.addSource(sourceId, { type: 'geojson', data: geojson });
      this.map.addLayer({
        id: 'trip-assist-layer',
        type: 'symbol',
        source: 'trip-assist',
        layout: {
          'icon-image': 'AiAssistTrip', // use the registered SVG
          'icon-size': 1,                       // adjust size if needed
          'icon-allow-overlap': true
        }
      });
    }
  }

  private updateTrafficSignals(): void {
    const sourceId = 'traffic-signals';
    const layerId = 'traffic-signals-layer';

    if (!this.trafficSignals?.length) {
      // Remove layer & source if they exist
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
      if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      return;
    }

    const features = this.trafficSignals.map((sig, i) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [sig.lng, sig.lat] },
      properties: { id: i, label: sig.label || '' }
    }));

    const geojson: any = {
      type: 'FeatureCollection',
      features
    };

    // Add or update source
    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      this.map.addSource(sourceId, { type: 'geojson', data: geojson });
      this.map.addLayer({
        id: 'traffic-signals-layer',
        type: 'symbol',
        source: 'traffic-signals',
        layout: {
          'icon-image': 'traffic-signal-icon', // use the registered SVG
          'icon-size': 2,                       // adjust size if needed
          'icon-allow-overlap': true
        }
      });
    }
  }

  private plotTunnelBridgeData(): void {
    const sourceId1 = 'tunnel-signals';
    const layerId1 = 'tunnel-signals-layer';
    const sourceId2 = 'bridge-signals';
    const layerId2 = 'bridge-signals-layer';

    if (!this.tunnelBridgeData?.tunnelData?.length) {
      // Remove layer & source if they exist
      if (this.map.getLayer(layerId1)) this.map.removeLayer(layerId1);
      if (this.map.getSource(sourceId1)) this.map.removeSource(sourceId1);
    } else {
      const features = this.tunnelBridgeData?.tunnelData?.map((sig, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [sig.lng, sig.lat] },
        properties: { id: i, label: sig.label || '' }
      }))

      const geojson: any = {
        type: 'FeatureCollection',
        features
      };

      // Add or update source
      if (this.map.getSource(sourceId1)) {
        (this.map.getSource(sourceId1) as maplibregl.GeoJSONSource).setData(geojson);
      } else {
        this.map.addSource(sourceId1, { type: 'geojson', data: geojson });
        this.map.addLayer({
          id: 'tunnel-signals-layer',
          type: 'symbol',
          source: 'tunnel-signals',
          layout: {
            'icon-image': 'tunnel-signal-icon',
            'icon-size': 1,
            'icon-allow-overlap': true
          }
        });
      }
    }

    if (!this.tunnelBridgeData?.bridgeData?.length) {
      // Remove layer & source if they exist
      if (this.map.getLayer(layerId2)) this.map.removeLayer(layerId2);
      if (this.map.getSource(sourceId2)) this.map.removeSource(sourceId2);
    } else {
      const features = this.tunnelBridgeData?.bridgeData?.map((sig, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [sig.lng, sig.lat] },
        properties: { id: i, label: sig.label || '' }
      }))

      const geojson: any = {
        type: 'FeatureCollection',
        features
      };

      // Add or update source
      if (this.map.getSource(sourceId2)) {
        (this.map.getSource(sourceId2) as maplibregl.GeoJSONSource).setData(geojson);
      } else {
        this.map.addSource(sourceId2, { type: 'geojson', data: geojson });
        this.map.addLayer({
          id: 'bridge-signals-layer',
          type: 'symbol',
          source: 'bridge-signals',
          layout: {
            'icon-image': 'bridge-signal-icon',
            'icon-size': 1,
            'icon-allow-overlap': true
          }
        });
      }
    }
  }

  private updateOptimizedRoute(): void {
    const sourceId = 'optimized-route';
    const layerId = 'optimized-route-layer';
    const hoverLayerId = 'optimized-route-hover-layer';

    if (!this.optimizedRouteData?.coordinates?.length) {

      if (this.map.getLayer(hoverLayerId)) this.map.removeLayer(hoverLayerId);
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
      if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);

      this.optimizedRouteHoverBound = false;
      return;
    }

    const coords = this.optimizedRouteData.coordinates.map(p => [p.lng, p.lat]);

    const geojson: any = {
      type: 'Feature',
      properties: {
        distanceInMtr: this.optimizedRouteData.travelSummary?.distanceInMtr,
        etaInSeconds: this.optimizedRouteData.travelSummary?.etaInSeconds
      },
      geometry: {
        type: 'LineString',
        coordinates: coords
      }
    };

    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojson);
      return;
    }

    this.map.addSource(sourceId, { type: 'geojson', data: geojson });

    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#196225ff',
        'line-width': 4,
        'line-opacity': 0.9
      }
    });

    this.map.addLayer({
      id: hoverLayerId,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-width': 20,
        'line-opacity': 0
      }
    });

    if (!this.optimizedRouteHoverBound) {
      this.bindOptimizedRouteHover();
      this.optimizedRouteHoverBound = true;
    }
  }


  private bindOptimizedRouteHover(): void {
    const hoverLayerId = 'optimized-route-hover-layer';

    this.map.on('mouseenter', hoverLayerId, e => {
      this.map.getCanvas().style.cursor = 'pointer';

      const feature = e.features?.[0];
      if (!feature) return;

      const props: any = feature.properties;

      const html = `
      <div style="min-width:160px">
        <b>Optimized Route</b><br/>
        Distance: ${(Number(props?.distanceInMtr) / 1000)?.toFixed(2) ?? '-'} km<br/>
        ETA: ${(Number(props?.etaInSeconds) / 60)?.toFixed(2) ?? '-'} mins
      </div>
    `;

      this.optimizedRoutePopup
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(this.map);
    });

    this.map.on('mouseleave', hoverLayerId, () => {
      this.map.getCanvas().style.cursor = '';
      this.optimizedRoutePopup.remove();
    });
  }


  private plotMapEvents(): void {
    const sourceId = 'map-events';
    const layerId = 'map-events-layer';

    // Remove if no events (same behavior as trafficSignals)
    if (!this.mapEvents?.length) {
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
      if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      return;
    }

    const features = this.mapEvents.map((ev, i) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [ev.lng, ev.lat]
      },
      properties: {
        id: i,
        icon: ev.icon === 'assets/images/over-speed-icon.svg'
          ? 'over-speed-icon'
          : 'default-event-icon',
        label: ev.label ?? '',
        alertName: ev.alertName ?? 'Speed Violation',
        speed: ev.speed ?? 0,
        speedLimit: ev.speedLimit ?? 0
      }
    }));

    const geojson: any = {
      type: 'FeatureCollection',
      features
    };

    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as maplibregl.GeoJSONSource)
        .setData(geojson);
    } else {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: geojson
      });

      this.map.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }

    const eventHoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    this.map.on('mousemove', layerId, (e) => {
      if (!e.features?.length) return;
      this.map.getCanvas().style.cursor = 'pointer';
      const feature = e.features[0];
      const props: any = feature.properties;

      const html = `
       <b>Alert: ${props?.alertName}</b><br>
       Speed: ${props?.speed ? Number(props?.speed)?.toFixed(2) : 0} Kmph<br>
       Limit: ${props?.speedLimit || '--'} Kmph
       `;

      eventHoverPopup.setLngLat(e.lngLat).setHTML(html).addTo(this.map);
    });

    this.map.on('mouseleave', layerId, () => {
      this.map.getCanvas().style.cursor = '';
      eventHoverPopup.remove();
    });
  }

  private plotTelemetryAlertCoordinates(): void {
    const sourceId = 'telemetry-events';
    const layerId = 'telemetry-events-layer';

    const run = () => {
      if (!this.telemetryAlertCoordinates?.length) {
        if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
        if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
        return;
      }

      const features = this.telemetryAlertCoordinates.map((ev, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [ev.lng, ev.lat] },
        properties: {
          id: i,
          icon: 'over-speed-icon',
          label: ev.label ?? '',
          alertName: 'Alert'
        }
      }));

      const geojson: any = { type: 'FeatureCollection', features };

      if (this.map.getSource(sourceId)) {
        (this.map.getSource(sourceId) as maplibregl.GeoJSONSource)
          .setData(geojson);
        return;
      }

      this.map.addSource(sourceId, { type: 'geojson', data: geojson });

      this.map.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    };

    if (!this.map.isStyleLoaded()) {
      this.map.once('styledata', run);
    } else {
      run();
    }
  }

  private drawSegmentHoverLayers(): void {
    if (!this.segmentDetails?.length || !this.map) return;

    const features = this.segmentDetails.map(segment => ({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [segment.firstCoordinate[1], segment.firstCoordinate[0]], // [lng, lat]
          [segment.lastCoordinate[1], segment.lastCoordinate[0]]
        ]
      },
      properties: {
        segmentId: segment.segmentId,
        latestSourceTimestamp: segment.latestSourceTimestamp,
        avgSpeed: segment.enhancedMap?.trafficData?.avgSpeedInKmph ?? '-',
        weather: segment.enhancedMap?.weatherData?.temperatureInCelsius ?? '-',
        temperature: segment.enhancedMap?.weatherData?.temperatureInCelsius ?? '-',
        airPressure: segment.enhancedMap?.weatherData?.airPressureInMillibar ?? '-',
        humidity: segment.enhancedMap?.weatherData?.humidityInPercentage ?? '-',
        windDirection: segment.enhancedMap?.weatherData?.windDirectionInDegree ?? '-',
        windVelocity: segment.enhancedMap?.weatherData?.windVelocityInKmph ?? '-'
      }
    }));

    const geojson: any = {
      type: 'FeatureCollection',
      features
    };

    const sourceId = 'segment-hover';
    const layerId = 'segment-hover-layer';

    // Add or update source
    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      this.map.addSource(sourceId, { type: 'geojson', data: geojson });

      // Add line layer (transparent but thick for easy hover)
      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': 'transparent',
          'line-width': 10
        }
      });
    }

    // Create a single popup for hover
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    this.map.on('mouseenter', layerId, (e) => {
      this.map.getCanvas().style.cursor = 'pointer';

      if (!e.features?.length) return;

      const feature = e.features[0];
      const props: any = feature.properties;

      const html = `
      <div style="min-width:180px">
        <b>Segment ID:</b> ${props.segmentId}<br>
        <b>Avg Speed:</b> ${props.avgSpeed} km/h<br>
        <b>Weather:</b> ${props.weather}°C<br>
        <b>Temperature:</b> ${props.temperature}°C<br>
        <b>Air Pressure:</b> ${props.airPressure} mb<br>
        <b>Humidity:</b> ${props.humidity}%<br>
        <b>Wind direction:</b> ${props.windDirection}°<br>
        <b>Wind Velocity:</b> ${props.windVelocity} km/h<br>
        <b>Last Updated:</b><br>${this.formatTimestamp(new Date(props.latestSourceTimestamp))}
      </div>
    `;

      popup.setLngLat(e.lngLat).setHTML(html).addTo(this.map);
    });

    this.map.on('mouseleave', layerId, () => {
      this.map.getCanvas().style.cursor = '';
      popup.remove();
    });
  }

  formatTimestamp(timestamp: string | Date): string {
    const date = new Date(timestamp);

    const day = date.getDate();
    const monthShort = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');

    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours; // convert 0 to 12

    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes}${ampm}`;

    return `${day} ${monthShort} ${year}, ${formattedTime}`;
  }

  rewind(): void {
    if (!this.trip?.length) return;

    const idx = this.findCurrentPointIndex();
    const prevIdx = Math.max(0, idx - 1);

    this.seek(this.trip[prevIdx].ts);
  }

  fastForward(): void {
    if (!this.trip?.length) return;

    const idx = this.findCurrentPointIndex();
    const nextIdx = Math.min(this.trip.length - 1, idx + 1);

    this.seek(this.trip[nextIdx].ts);
  }

  private findCurrentPointIndex(): number {
    if (!this.trip?.length) return 0;
    for (let i = 0; i < this.trip.length - 1; i++) {
      if (this.playhead >= this.trip[i].ts && this.playhead < this.trip[i + 1].ts) {
        return i;
      }
    }
    return this.trip.length - 1;
  }

  seek(ts: number): void {
    ts = Number(ts);

    this.isSeeking = true;
    this.playhead = ts;
    this.lastHeading = null;

    if (this.map && this.marker) {
      this.moveToTs(ts, true);
    }

    // 🔒 Restart playback from new position
    if (this.playing) {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.startPlayback();
    }
  }

  formatTime(ts: number): string {
    return new Date(ts).toLocaleTimeString();
  }

  // Total trip duration in seconds
  get totalDurationSec(): number {
    if (!this.trip?.length) return 0;
    return Math.floor((this.lastTs - this.firstTs) / 1000);
  }

  // Elapsed seconds from start
  get elapsedSec(): number {
    if (!this.playhead) return 0;
    return Math.floor((this.playhead - this.firstTs) / 1000);
  }

  formatDuration(seconds: number): string {
    if (seconds < 0 || !Number.isFinite(seconds)) return '00:00:00';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hrs, mins, secs]
      .map(val => val.toString().padStart(2, '0'))
      .join(':');
  }

  // private resetPlayback(): void {
  //   this.stopPlayback();

  //   // Move marker to first point
  //   const first = this.trip[0];
  //   this.marker.setLngLat([first.lng, first.lat]);

  //   // Update route source
  //   const src = this.map.getSource('trip-route') as maplibregl.GeoJSONSource;
  //   if (src) {
  //     src.setData({
  //       type: 'Feature',
  //       properties: {},
  //       geometry: {
  //         type: 'LineString',
  //         coordinates: this.trip.map(p => [p.lng, p.lat])
  //       }
  //     });
  //   }

  //   this.playhead = first.ts;
  // }

  ngOnDestroy(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.map) this.map.remove();
    this.loaderHandler.disableManualLoader();
  }

  /* ---------------- MAP INIT ---------------- */

  private initMap(): void {
    const playheadPoint = this.getPointForTs(this.playhead) ?? this.trip[0];
    const hereApiKey = this.apiKey;
    const hereStyle =
      this.styleUrl ||
      `https://az0blob0idec.blob.core.windows.net/blob-file-idec/face/MGM/mapstyle/idecHEREstyle.json?sp=r&st=2025-12-22T06:18:39Z&se=2026-12-17T14:33:39Z&spr=https&sv=2024-11-04&sr=b&sig=FQM3mvPDhlu5fenvXv%2F7kylBulbtSEd%2BsVcdtxzudDM%3D`;

    // Calculate initial bounds to avoid world map flash
    const bounds = new maplibregl.LngLatBounds();
    this.trip.forEach(p => bounds.extend([p.lng, p.lat]));
    const center = bounds.getCenter();

    if (this.playing) {
      this.map = new maplibregl.Map({
        container: this.mapContainer.nativeElement,
        style: hereStyle,
        center: [playheadPoint?.lng, playheadPoint?.lat],
        pitch: 100,
        zoom: 17.5,
        bearing: 0
      });
    } else {
      this.map = new maplibregl.Map({
        container: this.mapContainer.nativeElement,
        style: hereStyle,
        center: [center.lng, center.lat],
        zoom: 10
        // zoom: 17.5,
        // bearing: 0
      });
    }

    this.map.addControl(
      new maplibregl.NavigationControl({ visualizePitch: true }),
      'top-right'
    );

    this.map.on('load', () => {
      const img = new Image();
      img.onload = () => {
        if (!this.map.hasImage('traffic-signal-icon')) {
          this.map.addImage('traffic-signal-icon', img);
          this.updateTrafficSignals(); // refresh the layer with the new icon
        }
      };
      img.src = 'assets/images/traffic-signal.svg';
      const image2 = new Image();
      image2.onload = () => {
        if (!this.map.hasImage('over-speed-icon')) {
          this.map.addImage('over-speed-icon', image2);
          this.plotMapEvents(); // refresh the layer with the new icon
        }
      };
      image2.src = 'assets/images/over-speed-icon.svg';
      const image3 = new Image();
      image3.onload = () => {
        if (!this.map.hasImage('tunnel-signal-icon')) {
          this.map.addImage('tunnel-signal-icon', image3);
          this.plotTunnelBridgeData(); // refresh the layer with the new icon
        }
      };
      image3.src = 'assets/images/tunnel-signal-icon.svg';
      const image4 = new Image();
      image4.onload = () => {
        if (!this.map.hasImage('bridge-signal-icon')) {
          this.map.addImage('bridge-signal-icon', image4);
          this.plotTunnelBridgeData(); // refresh the layer with the new icon
        }
      };
      image4.src = 'assets/images/bridge-signal-icon.svg';
      const image5 = new Image();
      image5.onload = () => {
        if (!this.map.hasImage('AiAssistTrip')) {
          this.map.addImage('AiAssistTrip', image5);
          this._plotTripAssistPlots();
        }
      };
      image5.src = 'assets/images/AiAssistTrip.svg';
      const image6 = new Image();
      image6.onload = () => {
        if (!this.map.hasImage('location-start-geo')) {
          this.map.addImage('location-start-geo', image6);
        }
      };
      image6.src = 'assets/images/playback-start-location-icon.svg';
      const image7 = new Image();
      image7.onload = () => {
        if (!this.map.hasImage('location-end-geo')) {
          this.map.addImage('location-end-geo', image7);
        }
      };
      image7.src = 'assets/images/playback-end-location-icon.svg';
      const image8 = new Image();
      image8.onload = () => {
        if (!this.map.hasImage('highlightedIcon')) {
          this.map.addImage('highlightedIcon', image8);
        }
      };
      image8.src = 'assets/images/highlightedIcon.svg';
      this.mapReady = true;
      this.addRouteLayer();
      this.addStartEndMarkers();
      this.addEventMarkers();
      this.addVehicleMarker(playheadPoint);
      if (this.pendingTelemetryAlerts) {
        this.plotTelemetryAlertCoordinates();
        this.pendingTelemetryAlerts = false;
      }
    });
  }

  private getPointForTs(ts: number) {
    return this.trip?.find(p => p.ts >= ts) ?? this.trip[this.trip.length - 1];
  }

  private fitTripBounds(): void {
    const bounds = new maplibregl.LngLatBounds();

    this.trip.forEach(p => bounds.extend([p.lng, p.lat]));

    if (!bounds.isEmpty()) {
      this.map.jumpTo({ pitch: 0, bearing: 0 });
      this.map.fitBounds(bounds, {
        padding: 80,
        bearing: 0,
        duration: 0
      });
    }
  }


  /* ---------------- ROUTE ---------------- */

  private addRouteLayer(): void {
    if (!this.trip?.length || !this.map) return;

    const coords = this.trip.map(p => [p.lng, p.lat]);
    const sourceId = 'trip-route';
    const layerId = 'trip-route-line';

    const run = () => {

      // Remove existing layer first
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }

      // Then remove existing source
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }

      // Add fresh source
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords }
        }
      });

      // Add fresh layer
      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#1565c0',
          'line-width': 4
        }
      });

      if (!this.playing) {
        this.map.once('idle', () => {
          this.fitTripBounds();
        });
      }

    };

    // Ensure the style is loaded before adding route
    if (!this.map.isStyleLoaded()) {
      this.map.once('styledata', run);
    } else {
      run();
    }
  }

  private addStartEndMarkers(): void {
    if (!this.trip?.length) return;

    const start = this.trip[0];
    const end = this.trip[this.trip.length - 1];

    const data: any = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { icon: 'location-start-geo' },
          geometry: {
            type: 'Point',
            coordinates: [start.lng, start.lat]
          }
        },
        {
          type: 'Feature',
          properties: { icon: 'location-end-geo' },
          geometry: {
            type: 'Point',
            coordinates: [end.lng, end.lat]
          }
        }
      ]
    };

    if (this.map.getSource('start-end-source')) {
      (this.map.getSource('start-end-source') as any).setData(data);
      return;
    }

    this.map.addSource('start-end-source', {
      type: 'geojson',
      data
    });

    this.map.addLayer({
      id: 'start-end-layer',
      type: 'symbol',
      source: 'start-end-source',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': 1.2,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      }
    });
  }


  /* ---------------- EVENTS ---------------- */

  private addEventMarkers(): void {
    const features: any[] = [];

    this.trip?.forEach((p, idx) => {
      if (p.events?.length) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
          properties: {
            idx,
            label: p.events[0].label || p.events[0].type,
            type: p.events[0].type,
            timestamp: p.ts
          }
        });
      }
    });

    if (!features.length) return;

    this.map.addSource('trip-events', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features }
    });

    const img = new Image();
    img.onload = () => {
      if (!this.map.hasImage('event-marker')) {
        this.map.addImage('event-marker', img);
      }
    };
    img.src = this.makeEventSVG('!');

    this.map.addLayer({
      id: 'trip-events-symbol',
      type: 'symbol',
      source: 'trip-events',
      layout: {
        'icon-image': 'event-marker',
        'icon-size': 1,
        'icon-allow-overlap': true
      }
    });
  }

  private async registerMapEventIcons(): Promise<void> {
    if (this.map.hasImage('default-event-icon')) return;

    try {
      const trafficSignalImg: any = await this.map.loadImage(
        'assets/images/traffic-signal.svg'
      );
      this.map.addImage('default-event-icon', trafficSignalImg);

      const overspeedImg: any = await this.map.loadImage(
        'assets/images/over-speed-icon.svg'
      );
      this.map.addImage('over-speed-icon', overspeedImg);

    } catch (error) {
      console.error('Failed to load map event icons', error);
    }
  }

  /* ---------------- VEHICLE ---------------- */

  private addVehicleMarker(playheadPoint: any): void {
    const el = document.createElement('div');
    el.style.width = '56px';
    el.style.height = '56px';
    el.style.transformOrigin = '18px 18px';
    el.style.pointerEvents = 'none';

    // ⬇️ use asset SVG instead of inline SVG
    el.style.backgroundImage = `url(assets/images/truck-icon2.svg)`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.backgroundPosition = 'center';

    this.marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat([playheadPoint?.lng, playheadPoint?.lat])
      .addTo(this.map);
  }

  /* ---------------- PLAYBACK ---------------- */

  togglePlay(): void {
    if (!this.trip || this.trip.length < 2) return;

    if (this.playhead >= this.lastTs) {
      this.playhead = this.firstTs;
      this.lastHeading = null;
    }
    this.playing = !this.playing;
    this.isPlaying?.emit(this.playing)
    if (this.playing) this.startPlayback();
  }

  stop(): void {
    this.playing = false;
    this.playhead = this.firstTs;
    this.lastHeading = null;

    if (this.marker) {
      this.marker.setLngLat([this.trip[0].lng, this.trip[0].lat]);
    }

    this.fitTripBounds();
  }

  private startPlayback(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.findCurrentPointIndex() === 0) {
      this.lastHeading = null;
      const p0 = this.trip[0];
      const p1 = this.trip[Math.min(0 + 1, this.trip.length - 1)];

      const bearing = this.smoothHeading(
        this.computeStableBearing(p0, p1)
      );
      const camera: any = {
        center: [this.trip[0].lng, this.trip[0].lat],
        pitch: 100,
        zoom: 17.5,
        bearing,
        offset: [0, this.map.getCanvas().clientHeight * 0.4]
      }
      this.map.jumpTo(camera);
    }
    let startTime: number | null = null;
    const startPlayhead = Math.max(this.playhead, this.firstTs);

    const step = (now: number) => {
      if (!this.playing) return;
      if (!startTime) startTime = now;

      const elapsed = now - startTime;
      const currentTs = startPlayhead + elapsed * this.playbackRate;

      if (currentTs >= this.lastTs) {
        this.playhead = this.lastTs;
        this.moveToTs(this.lastTs);
        this.playing = false;
        this.isPlaying?.emit(this.playing)
        return;
      }

      this.moveToTs(currentTs);
      this.playhead = currentTs;
      this.rafId = requestAnimationFrame(step);
    };

    this.rafId = requestAnimationFrame(step);
  }

  // seek(ts: number): void {
  //   this.playhead = ts;
  //   this.lastHeading = null;
  //   this.moveToTs(ts);
  // }

  /* ---------------- MOTION ---------------- */

  private moveToTs(ts: number, instant = false): void {
    const i = this.findCurrentPointIndex();
    if (i !== this.lastSelectedIndex) {
      this.lastSelectedIndex = i;
      this.playbackStateUpdate?.emit({ ts: this.playhead, index: i });
    }
    const p0 = this.trip[i];
    const p1 = this.trip[Math.min(i + 1, this.trip.length - 1)];

    const dt = p1.ts - p0.ts || 1;
    const frac = Math.max(0, Math.min(1, (ts - p0.ts) / dt));

    const lat = p0.lat + (p1.lat - p0.lat) * frac;
    const lng = p0.lng + (p1.lng - p0.lng) * frac;

    this.marker.setLngLat([lng, lat]);

    const bearing = this.smoothHeading(
      this.computeStableBearing(p0, p1)
    );

    const camera: any = {
      center: [lng, lat],
      bearing,
      pitch: 100,
      zoom: 17.5,
      offset: [0, this.map.getCanvas().clientHeight * 0.4],
    };

    if (instant || this.isSeeking) {
      this.map.jumpTo(camera);     // immediate render
    } else {
      this.map.easeTo({
        ...camera,
        duration: 300,
        offset: [0, this.map.getCanvas().clientHeight * 0.4],
        easing: t => t
      });
    }
  }


  private distanceMeters(
    lat1: number, lng1: number,
    lat2: number, lng2: number
  ): number {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;

    return 2 * R * Math.asin(Math.sqrt(a));
  }

  private computeStableBearing(
    p0: TripPoint,
    p1: TripPoint
  ): number {
    const dist = this.distanceMeters(p0.lat, p0.lng, p1.lat, p1.lng);

    if (dist < this.HEADING_EPSILON_METERS && this.lastHeading != null) {
      return this.lastHeading;
    }

    const raw =
      (Math.atan2(p1.lng - p0.lng, p1.lat - p0.lat) * 180) / Math.PI;

    return (raw + 360) % 360;
  }


  private findSegmentIndex(ts: number): number {
    if (ts <= this.trip[0].ts) return 0;
    if (ts >= this.lastTs) return this.trip.length - 2;
    for (let i = 0; i < this.trip.length - 1; i++) {
      if (ts >= this.trip[i].ts && ts <= this.trip[i + 1].ts) return i;
    }
    return this.trip.length - 2;
  }

  private smoothHeading(h: number): number {
    if (this.lastHeading == null) {
      this.lastHeading = h;
      return h;
    }
    let diff = h - this.lastHeading;
    while (diff > 180) diff -= 360;
    while (diff < -180) diff += 360;
    this.lastHeading += diff * 0.18;
    return this.lastHeading;
  }

  /* ---------------- SVG HELPERS ---------------- */

  private makeEventSVG(label: string): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
      <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'>
        <circle cx='14' cy='14' r='12' fill='#e53935' stroke='white' stroke-width='2'/>
        <text x='14' y='19' font-size='12' text-anchor='middle' fill='white'>${label}</text>
      </svg>
    `)}`;
  }

  private makeVehicleSVG(): string {
    return `
      <svg width='36' height='36' viewBox='0 0 36 36'>
        <rect x='8' y='9' width='20' height='12' rx='3'
              fill='#2e7d32' stroke='#1b5e20' stroke-width='1.2'/>
        <circle cx='13' cy='24' r='2.6' fill='#111'/>
        <circle cx='23' cy='24' r='2.6' fill='#111'/>
      </svg>
    `;
  }
}
